// ─────────────────────────────────────────────────────────────────────────────
// RF-150 / C2 — «Históricos por Campaña», el panel `rep-panel-historicos` de la maqueta.
//
// 🔴 Este tab pintaba SOLO el ranking de personas de una campaña elegida en un desplegable. La
// maqueta pide aquí una TABLA COMPARATIVA de campañas —Nombre · Tipo · Estado · Audiencia ·
// Alcance · Reconocimientos · Origen · Expiración · Acciones— que hasta ahora vivía, incompleta,
// en el tab de Campañas General. Los dos tabs tenían el contenido cruzado.
//
// UAT 17-ago-2026: «Frecuencia» salió (no está en las columnas de la Sección 7 de la Especificación)
// y entró «Alcance» — a qué OUs se dirigió la campaña, que era la pregunta que nadie respondía.
//
// El ranking NO se pierde: pasa a ser el DRILL-DOWN de la tabla. Se pulsa una campaña y su ranking
// aparece debajo — que es lo que el desplegable hacía, pero con la comparativa delante. Regla del
// módulo: restilar y recolocar, nunca perder una función.
//
// «Origen» no necesitó contrato: es `parentCampaignId` — raíz = Original, hija = Local.
//
// Sin columna "Puntos" a propósito: ver nota en HistoryReportDto (regla de oro #1).
//
// 2026-09-02 — «Acciones» (el «Descargar Excel» por fila, `CampaignRowActions`) se retiró de esta
// tabla a petición explícita del cliente: con el botón de `CampaignsGlobalExportButton` ya en la
// cabecera de la tarjeta (reporte de TODAS las campañas en un solo Excel), la descarga por fila
// individual no debe verse aquí. `CampaignRowActions` sigue viva en `CampaignsGeneralTab` — no se
// tocó ese tab, solo se dejó de montar en este.
// ─────────────────────────────────────────────────────────────────────────────
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCampaigns, useCampaignSummaries, useRecognitionHistory, getCampaignName } from '@/api/recognition';
import type { CampaignDto } from '@/api/recognition';
import { CampaignsGlobalExportButton } from './CampaignsGlobalExportButton';
import { exportToExcel } from '@/utils/exportExcel';
import { usePersonNameResolver } from '@/views/AnalyticsView/hooks/useAnalyticsNames';
import { ReportStatus, withResolvedNames, AlcanceCell, KpiRow } from '../reportsShared';
import { Paginacion, usePaginacion } from '../Paginacion';
import { tasaDeParticipacion, formatTasaDisplay } from './CampaignsGeneralTab';

// RF-150 — este tab NO recibe los filtros superiores a propósito: se ordena por campaña, no por
// rango de fechas, y su único filtro real es la campaña que se elige abajo. Antes aceptaba `params`
// y lo descartaba con `void`; ahora no lo recibe, y `RecognitionAnalyticsView` oculta la barra
// mientras este tab está activo (D-13), para que no queden tags visibles que la tabla ignora.
/**
 * C2 — «Origen» de la maqueta. No hizo falta abrir contrato: una campaña con `parentCampaignId` es
 * una variante LOCAL (la excepción por OU se modela como campaña hija, GAP-004); sin él, es la raíz.
 *
 * Aparte del render para poder probarlo: es la única columna de esta tabla que se DERIVA en vez de
 * leerse, y por tanto la única que puede equivocarse en silencio.
 */
export function origenDe(campaign: { parentCampaignId?: string | null }): 'original' | 'local' {
  return campaign.parentCampaignId ? 'local' : 'original';
}

/** Exportado para que `CampaignsGlobalExportButton` use el MISMO mapeo de estado que esta tabla:
 *  dos criterios distintos para el mismo dato (este vs. `campaignDisplayStatus` de `CampaignRowActions`)
 *  no pueden coexistir en el reporte que exporta EXACTAMENTE lo que esta tabla ya muestra. */
export function estadoVisible(estado: CampaignDto['estado']): 'activo' | 'expirado' | 'borrador' {
  if (estado === 'Finalizada') return 'expirado';
  if (estado === 'Borrador') return 'borrador';
  return 'activo';
}

/**
 * Colores del badge de Estado/Origen. Copia local de `adm-badge*` (`recognition.css`): ese archivo
 * no se carga en la ruta directa de Reportería (ver nota de `.rr-sr-only` en `RecognitionReports.css`),
 * así que este módulo necesita sus propias clases en vez de depender de otra pantalla ya cargada.
 */
function estadoBadgeClass(estado: ReturnType<typeof estadoVisible>): string {
  if (estado === 'activo') return 'rr-badge rr-badge--on';
  if (estado === 'borrador') return 'rr-badge rr-badge--warn';
  return 'rr-badge';
}

function origenBadgeClass(origen: ReturnType<typeof origenDe>): string {
  return origen === 'original' ? 'rr-badge rr-badge--on' : 'rr-badge rr-badge--info';
}

/**
 * 3 combinaciones de color para la barra de Reconocimientos, una por tercio del % de la fila
 * contra el máximo de la página: baja participación en rojo → rojo más fuerte, media en
 * naranja → amarillo, alta en verde → azul. Cada tercio es su propio gradiente de 2 colores —
 * pedido explícito: bandas discretas, no un cruce continuo entre las tres.
 *
 * El rojo y el naranja/verde son `--cp-red`/`--cp-orange`/`--cp-green` de `tokens.css`; el azul es
 * `--cp-cyan`. El rojo fuerte y el amarillo no tienen token propio en la paleta (no hay un "rojo
 * oscuro" ni un "amarillo" declarados): el rojo fuerte es el mismo `--cp-red` oscurecido a mano
 * (mismo rojo, no uno inventado), y el amarillo es un ámbar que empareja con `--cp-orange`.
 */
const BANDAS_RECONOCIMIENTOS: ReadonlyArray<readonly [string, string]> = [
  ['#E11F26', '#8C1216'], // baja: rojo → rojo más fuerte
  ['#F08521', '#FFD54F'], // media: naranja → amarillo
  ['#20B15A', '#40B8E9'], // alta: verde → azul
];

/** Gradiente de la barra de Reconocimientos según el tercio en el que caiga `pct` (0-100).
 *  Exportada para poder probar las 3 bandas y sus límites. */
export function reconocimientosBarGradient(pct: number): string {
  const p = Math.min(100, Math.max(0, pct));
  let indice = 2;
  if (p < 33) {
    indice = 0;
  } else if (p < 66) {
    indice = 1;
  }
  const [desde, hasta] = BANDAS_RECONOCIMIENTOS[indice];
  return `linear-gradient(to right, ${desde}, ${hasta})`;
}

/** `canExportData` default `true`: ver nota en `DailyTab.tsx`. */
const SIN_DATO = '—';

export function HistoryTab({ canExportData = true }: Readonly<{ canExportData?: boolean }> = {}) {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language.split('-')[0];
  const [campaignId, setCampaignId] = useState('');
  const [search, setSearch] = useState('');

  const campaigns = useCampaigns(undefined, 1, 100);
  // La maqueta lista TODAS, borradores incluidos: su fila de ejemplo «Onboarding Nuevos» está en
  // Borrador, sin fecha ni métricas. Es la comparativa histórica, no el catálogo de lo publicado.
  const lista = useMemo(() => campaigns.data?.items ?? [], [campaigns.data]);
  const ids = useMemo(() => lista.map((c) => c.id), [lista]);
  const resumenes = useCampaignSummaries(ids);

  /*
   * 🔴 Cada campaña viaja con SU resumen adosado, y se pagina el resultado.
   *
   * Antes la tabla leía `resumenes[i]` con la `i` de la PÁGINA, mientras que `resumenes` está
   * indexado por la lista COMPLETA (`useQueries` conserva el orden de `ids`). En la página 2,
   * `visibles[0]` es `lista[10]` pero `resumenes[0]` es el resumen de `lista[0]`: las columnas
   * «Audiencia» y «Reconocimientos» enseñaban las cifras de OTRA campaña, y sin ningún síntoma —
   * son números plausibles.
   *
   * Adosar el resumen ANTES de rebanar es lo que lo hace robusto: reindexar con
   * `(pagina - 1) * tamano + i` también lo arreglaría hoy, pero volvería a romperse en silencio en
   * cuanto alguien añada un orden o un buscador a esta tabla. Es el patrón que ya usa
   * `CampaignsGeneralTab`.
   */
  const filasCampana = useMemo(
    () => lista.map((campana, i) => ({
      campana,
      resumen: resumenes[i]?.data,
      cargando: resumenes[i]?.isLoading ?? false,
    })),
    [lista, resumenes],
  );

  /*
   * Especificación Funcional, tabla «Historial de Campañas personalizadas»: «buscador por nombre,
   * ordenamiento y paginación». La Sección 7 SÍ prohíbe los filtros superiores (OU/BU/Site/Depto/
   * Persona/Año-Mes) en Históricos por estar «ya segmentado por campaña» — pero un buscador por
   * nombre no es uno de esos, es el mismo patrón que ya usa `CampaignsGeneralTab` (RF-151).
   */
  const filasFiltradas = useMemo(
    () => filasCampana.filter(({ campana }) => (
      getCampaignName(campana, lang).toLowerCase().includes(search.trim().toLowerCase())
    )),
    [filasCampana, search, lang],
  );

  const q = useRecognitionHistory({ campaignId }, Boolean(campaignId));
  const data = q.data;
  const personIds = useMemo(() => data?.personas.map((entrada) => entrada.id) ?? [], [data]);
  const { resolvePersonName } = usePersonNameResolver(personIds);
  const resolvedPersonas = useMemo(
    () => (data ? withResolvedNames(data.personas, resolvePersonName) : []),
    [data, resolvePersonName],
  );

  /**
   * Los 4 KPIs de la maqueta: Total · Participantes · Participación · Promedio por participante.
   *
   * 🔴 Son de UNA campaña, nunca la suma de todas. De los cuatro, solo «Total» se podría sumar entre
   * campañas: «Participantes» cuenta personas distintas, y la misma persona en dos campañas contaría
   * dos veces — con lo que la participación y el promedio saldrían mal sin que nada lo delatara. La
   * maqueta tampoco los suma: los muestra de la campaña que se elige en su selector, y la Sección 7
   * dice que este panel «ya está segmentado por campaña».
   *
   * No hace falta pedir nada: `resumenes` ya trae el de cada campaña, indexado igual que `ids`.
   */
  const kpisCampana = useMemo(() => {
    if (!campaignId) return null;
    const i = ids.indexOf(campaignId);
    return i >= 0 ? (resumenes[i]?.data ?? null) : null;
  }, [campaignId, ids, resumenes]);

  /*
   * Tasa y promedio se DERIVAN, no se piden: son cociente de dos cifras que ya están en el resumen.
   * Se reutiliza `tasaDeParticipacion` de «Campañas General» en vez de reescribir la división, para
   * que las dos pantallas no puedan discrepar en el redondeo ni en el caso «audiencia desconocida».
   *
   * 🔴 Con el resumen SUPRIMIDO por el umbral de anonimato las dos salen «—»: se derivan del conteo
   * de participantes, así que publicarlas abriría por la puerta de al lado lo que el umbral cierra.
   */
  const tasaCampana = useMemo(() => {
    if (!kpisCampana || kpisCampana.suprimido) return null;
    const t = tasaDeParticipacion(kpisCampana.participantes, kpisCampana.audiencia);
    // `formatTasaDisplay`: mismo redondeo a 1 decimal (y el mismo aviso `<0.1%`) que la tabla de
    // «Campañas General» — es justo la divergencia que el comentario de arriba dice evitar.
    return t == null ? null : formatTasaDisplay(t, false);
  }, [kpisCampana]);

  const promedioCampana = useMemo(() => {
    if (!kpisCampana || kpisCampana.suprimido) return null;
    // Sin participantes no hay promedio: dividir por cero no da 0, no da nada.
    if (!kpisCampana.participantes) return null;
    return (kpisCampana.reconocimientos / kpisCampana.participantes).toFixed(1);
  }, [kpisCampana]);

  /*
   * Dos tablas, dos paginaciones independientes: la comparativa de campañas y el ranking de la
   * campaña elegida. Avanzar en una no debe mover la otra.
   *
   * El ranking es donde más hace falta: el backend devuelve hasta `MaxRows = 1000` filas, y hasta
   * ahora se pintaban todas de golpe.
   */
  const pagCampanas = usePaginacion(filasFiltradas);
  const pagRanking = usePaginacion(resolvedPersonas);

  // La barra de Reconocimientos compara contra el máximo de la PÁGINA visible, no del total: es un
  // apoyo visual para leer la tabla que tienes delante, no una métrica que deba sobrevivir a paginar.
  const maxReconocimientosPagina = useMemo(
    () => pagCampanas.visibles.reduce((max, { resumen }) => Math.max(max, resumen?.reconocimientos ?? 0), 0),
    [pagCampanas.visibles],
  );

  const isEmpty = !!data && data.personas.length === 0;

  /*
   * Fix final (revisión de rama) — la migración xlsx→exceljs volvió async a `exportToExcel` y
   * `handleExport` quedó sin try/catch: un fallo (401/500) rechazaba la promesa SIN manejar — cero
   * mensaje, cero log. Mismo hallazgo ya corregido en `handleExportShine` (`ShineTab.tsx`, fix round
   * 1); el mensaje se REUSA de `reports.shine.exporting`/`exportError` (ya genérico) en vez de
   * duplicarlo.
   */
  const [exportandoHistorico, setExportandoHistorico] = useState(false);
  const [exportHistoricoError, setExportHistoricoError] = useState(false);

  async function handleExport() {
    if (!data) return;
    setExportandoHistorico(true);
    setExportHistoricoError(false);
    try {
      const rows = resolvedPersonas.map((p, i) => ({
        [t('reports.history.colPos')]: i + 1,
        [t('reports.history.colName')]: p.nombre,
        [t('reports.history.colNominations')]: p.count,
      }));
      await exportToExcel(rows, `historico-${campaignId || 'campaign'}`);
    } catch (error: unknown) {
      if (import.meta.env.DEV) console.error('No se pudo generar el export de Historico:', error);
      setExportHistoricoError(true);
    } finally {
      setExportandoHistorico(false);
    }
  }

  return (
    <div className="au-card">
      <div className="au-card-header">
        <div>
          <div className="au-card-title">
            <i className="fas fa-chart-line" aria-hidden="true" /> {t('reports.history.title')}
          </div>
          <div className="au-card-sub">{t('reports.history.subtitle')}</div>
        </div>
        {/* P18-ter — reporte de TODAS las campañas en un solo Excel, distinto del «Descargar Excel»
            por fila (`CampaignRowActions`, una campaña) y del de más abajo (ranking de la campaña
            elegida). Va en la cabecera de la tarjeta porque aplica al listado completo, no a una fila
            ni a una selección. */}
        <CampaignsGlobalExportButton campaigns={lista} canExportData={canExportData} />
      </div>

      <div className="rr-actions">
        <input
          type="text"
          className="au-date-input"
          placeholder={t('reports.history.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label={t('reports.history.search')}
        />
      </div>

      {filasFiltradas.length === 0 ? (
        <div className="rr-state">{t('reports.common.empty')}</div>
      ) : (
      <>
      {/* La tabla comparativa de la maqueta. La FILA es el selector: pulsarla abre abajo el ranking
          de esa campaña, que es lo que antes hacía el desplegable. */}
      <div className="rr-table-wrap">
        <table className="rr-table">
          <thead>
            <tr>
              <th>{t('reports.history.colCampaign')}</th>
              <th>{t('reports.history.colType')}</th>
              <th>{t('reports.history.colStatus')}</th>
              <th className="rr-num" title={t('reports.common.colAudienceHelp')}>{t('reports.history.colAudience')}</th>
              <th title={t('reports.common.colScopeHelp')}>{t('reports.common.colScope')}</th>
              <th className="rr-num">{t('reports.history.colRecognitions')}</th>
              <th>{t('reports.history.colOrigin')}</th>
              <th>{t('reports.history.colExpiration')}</th>
            </tr>
          </thead>
          <tbody>
            {pagCampanas.visibles.map(({ campana: c, resumen, cargando }) => {
              const cifra = (v: number | undefined) => {
                if (resumen?.suprimido) return '—';
                if (v !== undefined) return v;
                return cargando ? '…' : '—';
              };
              // `undefined` si está suprimido por anonimato: sin esto, la barra revelaría por
              // color/ancho lo que el umbral de anonimato tapa en el número.
              const reconocimientosVisible = resumen?.suprimido ? undefined : resumen?.reconocimientos;
              const pctReconocimientos = typeof reconocimientosVisible === 'number' && maxReconocimientosPagina > 0
                ? Math.round((reconocimientosVisible / maxReconocimientosPagina) * 100)
                : 0;
              return (
                <tr
                  key={c.id}
                  className={c.id === campaignId ? 'rr-row--on' : undefined}
                  onClick={() => setCampaignId(c.id === campaignId ? '' : c.id)}
                >
                  <td>{getCampaignName(c, lang)}</td>
                  <td>{t(`reports.campaigns.type.${c.tipo}`)}</td>
                  <td>
                    <span className={estadoBadgeClass(estadoVisible(c.estado))}>
                      {t(`reports.history.status.${estadoVisible(c.estado)}`)}
                    </span>
                  </td>
                  <td className="rr-num">{cifra(resumen?.audiencia)}</td>
                  <td><AlcanceCell alcance={c.alcance ?? []} /></td>
                  {/* La columna «Frecuencia» se retiró (UAT 17-ago-2026, a petición de Adriana): no
                      está en la lista de columnas de la Sección 7 de la Especificación Funcional
                      —Nombre · Tipo · Estado · Audiencia · Reconocimientos · Expiración— y en la
                      práctica salía «—» en casi todas las filas, porque `frecuencia` es declarativa
                      y opcional en la campaña. El campo del contrato NO se toca: lo usa el wizard de
                      campañas. «Acciones» también salió de esta lista (2026-09-02, ver nota de
                      cabecera del archivo). */}
                  <td className="rr-num">
                    <div className="rr-num-cell">
                      <span>{cifra(resumen?.reconocimientos)}</span>
                      {typeof reconocimientosVisible === 'number' && (
                        <div className="rr-bar" aria-hidden="true">
                          <div
                            className="rr-bar-fill"
                            style={{
                              width: `${pctReconocimientos}%`,
                              background: reconocimientosBarGradient(pctReconocimientos),
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={origenBadgeClass(origenDe(c))}>
                      {t(`reports.history.origin.${origenDe(c)}`)}
                    </span>
                  </td>
                  <td>{c.fechaFin ? new Date(c.fechaFin).toLocaleDateString() : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Paginacion
        pagina={pagCampanas.pagina} totalPaginas={pagCampanas.totalPaginas} total={pagCampanas.total}
        tamano={pagCampanas.tamano} onPagina={pagCampanas.setPagina} onTamano={pagCampanas.cambiarTamano}
      />
      </>
      )}

      {canExportData && (
        <div className="rr-actions rr-actions--top-gap">
          <button
            type="button"
            className="rr-btn"
            onClick={handleExport}
            disabled={!data || data.personas.length === 0 || exportandoHistorico}
          >
            <i
              className={exportandoHistorico ? 'fas fa-spinner fa-spin' : 'fas fa-file-excel'}
              aria-hidden="true"
            />{' '}
            {exportandoHistorico ? t('reports.shine.exporting') : t('reports.common.exportExcel')}
          </button>
        </div>
      )}

      {/* Mismo hallazgo que `handleExportShine` (fix round 1, `ShineTab.tsx`): sin esto un fallo del
          export quedaba en silencio, sin mensaje ni log. */}
      {exportHistoricoError && (
        <p className="rr-state rr-state--error" role="alert">
          <i className="fas fa-triangle-exclamation" aria-hidden="true" /> {t('reports.shine.exportError')}
        </p>
      )}

      {!campaignId ? (
        <div className="rr-state">{t('reports.history.pickCampaign')}</div>
      ) : (
        <ReportStatus
          isLoading={q.isLoading}
          isError={q.isError}
          error={q.error}
          isEmpty={isEmpty}
          onRetry={() => q.refetch()}
        >
          {/*
            Los 4 KPIs van AQUÍ y no arriba como en la maqueta: en ella el selector es un desplegable
            y siempre hay campaña elegida, mientras que aquí el selector es la FILA de la tabla. Una
            fila de KPIs arriba estaría vacía hasta que alguien pulsa, y cuatro «—» en la cabecera se
            leen como un fallo de carga. Aparecen con el ranking de la campaña, que es lo que revela
            la selección.
          */}
          {kpisCampana && (
            <KpiRow
              items={[
                {
                  label: t('reports.history.kpiTotal'),
                  value: kpisCampana.suprimido ? SIN_DATO : kpisCampana.reconocimientos,
                  color: '#342F82',
                },
                {
                  label: t('reports.history.kpiParticipants'),
                  value: kpisCampana.suprimido ? SIN_DATO : kpisCampana.participantes,
                  color: '#40B8E9',
                },
                {
                  label: t('reports.history.kpiParticipation'),
                  value: tasaCampana ?? SIN_DATO,
                  color: '#20B15A',
                },
                {
                  label: t('reports.history.kpiAvgPerPerson'),
                  value: promedioCampana ?? SIN_DATO,
                  color: '#F08521',
                },
              ]}
            />
          )}

          {data && data.personas.length > 0 && (
            <div className="rr-table-wrap">
              <table className="rr-table">
                <thead>
                  <tr>
                    <th>{t('reports.history.colPos')}</th>
                    <th>{t('reports.history.colName')}</th>
                    <th className="rr-num">{t('reports.history.colNominations')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pagRanking.visibles.map((p, i) => (
                    <tr key={p.id || i}>
                      {/* 🔴 Posición ABSOLUTA en el ranking, no el índice dentro de la página: con
                          `i + 1`, la página 2 volvía a empezar en «1» y decía que el 11.º es el
                          primero. */}
                      <td>{(pagRanking.pagina - 1) * pagRanking.tamano + i + 1}</td>
                      <td>{p.suprimido ? <span className="rr-muted">{t('reports.common.masked')}</span> : p.nombre}</td>
                      <td className="rr-num">{p.suprimido ? '—' : p.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Paginacion
                pagina={pagRanking.pagina} totalPaginas={pagRanking.totalPaginas} total={pagRanking.total}
                tamano={pagRanking.tamano} onPagina={pagRanking.setPagina} onTamano={pagRanking.cambiarTamano}
              />
            </div>
          )}
        </ReportStatus>
      )}
    </div>
  );
}
